import PropTypes from 'prop-types';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';
import { movementSense, formatDelta } from '../../utils/simulationEngine';

/**
 * Simulation — participant screen.
 *
 * This is what a person sees on their phone. It follows the same shell as
 * PollActivity (full-bleed white, sticky logo header, session-code chip, primary-50
 * type banner) and is deliberately light-only, matching the other participant screens.
 *
 * REDACTION. This component never receives `whenChosen`, `leadershipAngle`,
 * `behaviour` or `kpi` for an unrevealed card — the server strips them before the
 * payload leaves. It defends the boundary a second time by simply not reading those
 * fields until `phase === 'reveal' | 'debrief'`, and by never rendering a
 * leadershipAngle at all: the coaching insight belongs to the final debrief on the room
 * screen, after the last round. Neither defence is sufficient alone; do not remove the
 * server one on the strength of this one.
 *
 * Participants also only ever get THEIR OWN function's deck. Sending all four and
 * filtering client-side would put every team's options in the network tab.
 */

const PHASE_LABEL = {
  brief: 'THE SITUATION',
  negotiate: 'TALK IT OUT',
  decide: 'YOUR CALL',
  locked: 'LOCKED IN',
  reveal: 'WHAT EVERYONE CHOSE',
  debrief: 'WHAT EVERYONE CHOSE',
  closed: 'FINAL DEBRIEF',
};

const Shell = ({ sessionCode, phase, timeRemaining, onBackToList, children }) => (
  <div className="min-h-screen bg-white">
    <div className="sticky top-0 z-50 border-b border-neutral-200 bg-white shadow-sm">
      <div className="mx-auto max-w-6xl px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBackToList && (
              <button onClick={onBackToList} className="text-neutral-600 transition-colors hover:text-neutral-900" aria-label="Back">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="flex items-center">
              <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
              <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {typeof timeRemaining === 'number' && (
              <div
                className={`rounded-full border px-2.5 py-1 ${
                  timeRemaining === 0 ? 'border-red-200 bg-red-50'
                    : timeRemaining <= 30 ? 'border-amber-200 bg-amber-50'
                    : 'border-neutral-300 bg-neutral-100'
                }`}
              >
                <span
                  className={`text-xs font-semibold tabular-nums ${
                    timeRemaining === 0 ? 'text-red-600' : timeRemaining <= 30 ? 'text-amber-700' : 'text-neutral-600'
                  }`}
                >
                  {timeRemaining === 0
                    ? 'Time’s up'
                    : `${Math.floor(timeRemaining / 60)}:${String(timeRemaining % 60).padStart(2, '0')}`}
                </span>
              </div>
            )}
            {sessionCode && (
              <div className="rounded-md bg-orange-100 px-2.5 py-1">
                <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    <div className="bg-primary-50 px-6 py-3">
      <div className="mx-auto max-w-6xl">
        <h2 className="text-lg font-bold tracking-wide text-primary-600">{PHASE_LABEL[phase] || 'SIMULATION'}</h2>
      </div>
    </div>

    <div className="mx-auto max-w-3xl px-5 py-6 pb-24">{children}</div>
  </div>
);

const SimulationActivity = ({
  view,
  roundIndex = 1,
  phase = 'brief',
  myFunctionKey,
  selectedCardId = null,
  lockedCardId = null,
  lockedCount = 0,
  reveal = null,
  sessionCode,
  timeRemaining,
  onSelect,
  onLockIn,
  onBackToList,
  onBackToSession,
}) => {
  // `view` is the SERVER-REDACTED payload (or its preview-side mirror). It already
  // contains only this team's deck; there is nothing here to filter, and nothing to
  // accidentally over-render.
  const round = view?.rounds?.find((r) => r.index === roundIndex) || view?.rounds?.[0];
  const me = view?.functions?.find((f) => f.key === myFunctionKey);
  if (!view || !round || !me) return null;

  const deck = view.deck || [];
  const teamCount = view.functions?.length || 0;

  return (
    <Shell sessionCode={sessionCode} phase={phase} timeRemaining={timeRemaining} onBackToList={onBackToList}>
      {/* who am I — persistent, because people forget which team they're on */}
      <div className="mb-5 flex items-center gap-2.5 rounded-xl bg-neutral-900 px-4 py-3">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">You are</span>
        <span className="text-sm font-bold text-white">{me.name}</span>
        <span className="ml-auto text-xs text-neutral-400">Round {roundIndex}</span>
      </div>

      {/* ---------------- BRIEF / NEGOTIATE ---------------- */}
      {(phase === 'brief' || phase === 'negotiate') && (
        <>
          <h3 className="text-xl font-bold leading-snug text-neutral-900">{round.title}</h3>
          <p className="mt-2 text-[15px] leading-relaxed text-neutral-700">{round.context}</p>

          {round.conflict?.length > 0 && (
            <div className="mt-5 space-y-2">
              {round.conflict.map((line, i) => (
                <div key={i} className="rounded-xl bg-neutral-50 px-3.5 py-2.5">
                  <span className="text-xs font-bold uppercase tracking-wide text-neutral-500">{line.speakerLabel}</span>
                  <span className="text-xs text-neutral-400"> {line.verb}</span>
                  <p className="mt-0.5 text-sm text-neutral-800">&ldquo;{line.statement}&rdquo;</p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-wide text-primary-700">Your job</p>
            <p className="mt-1 text-sm text-neutral-900">{round.challenge}</p>
          </div>

          <div className="mt-5 rounded-xl bg-neutral-900 px-4 py-3.5">
            <p className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">{me.name} — only you can see this</p>
            <p className="mt-1 text-sm text-white">{me.mandate}</p>
            {me.privateBrief && <p className="mt-1.5 text-sm leading-relaxed text-neutral-300">{me.privateBrief}</p>}
          </div>

          {phase === 'negotiate' && (
            <div className="mt-5 rounded-xl border-2 border-dashed border-neutral-300 px-4 py-5 text-center">
              <p className="text-base font-bold text-neutral-900">Go and talk to the other teams</p>
              <p className="mt-1 text-sm text-neutral-600">
                You can&apos;t fix this alone. Find out what the others are about to do — before you
                commit.
              </p>
            </div>
          )}
        </>
      )}

      {/* ---------------- DECIDE ---------------- */}
      {phase === 'decide' && (
        <>
          <h3 className="text-lg font-bold text-neutral-900">What does {me.name} do?</h3>
          <p className="mt-1 text-sm text-neutral-600">
            Pick one. Nobody sees your choice until every team has locked in.
          </p>

          <div className="mt-4 space-y-2.5">
            {deck.map((card) => {
              const isSelected = selectedCardId === card.id;
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => onSelect?.(card.id)}
                  aria-pressed={isSelected}
                  className={`w-full rounded-2xl border-2 p-4 text-left transition-all ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-neutral-200 bg-white hover:border-neutral-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
                        isSelected ? 'bg-primary-500 text-white' : 'bg-neutral-100 text-neutral-500'
                      }`}
                    >
                      {card.order}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[15px] font-semibold leading-snug text-neutral-900">{card.title}</p>
                      <p className="mt-0.5 text-sm leading-relaxed text-neutral-600">{card.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* sticky, not fixed — `fixed` is relative to the viewport, so it escapes any
              container the screen is embedded in (the trainer's phone-frame preview).
              Sticky pins to the bottom of the scroll area in both contexts. */}
          <div className="sticky bottom-0 -mx-5 mt-4 border-t border-neutral-200 bg-white px-5 py-3.5">
            <button
              type="button"
              disabled={!selectedCardId}
              onClick={() => onLockIn?.(selectedCardId)}
              className="w-full rounded-xl bg-primary-500 py-3.5 text-base font-bold text-white transition-colors hover:bg-primary-600 disabled:bg-neutral-200 disabled:text-neutral-400"
            >
              {selectedCardId ? 'Lock in our decision' : 'Choose one to continue'}
            </button>
          </div>
        </>
      )}

      {/* ---------------- LOCKED ---------------- */}
      {phase === 'locked' && (
        <div className="py-10 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <svg className="h-8 w-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="mt-4 text-xl font-bold text-neutral-900">Locked in</h3>
          {lockedCardId && (
            <p className="mt-1 text-sm text-neutral-600">
              {deck.find((c) => c.id === lockedCardId)?.title}
            </p>
          )}
          <p className="mt-5 text-sm font-medium text-neutral-500">
            {lockedCount} of {teamCount} teams have decided
          </p>
          <div className="mx-auto mt-2 h-1.5 w-40 overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${(lockedCount / teamCount) * 100}%` }}
            />
          </div>
          <p className="mt-5 text-xs text-neutral-400">Waiting for the others. Nobody can see what you picked.</p>
        </div>
      )}

      {/* ---------------- REVEAL / DEBRIEF ---------------- */}
      {(phase === 'reveal' || phase === 'debrief') && reveal && (
        <>
          <h3 className="text-lg font-bold text-neutral-900">Every team, at once</h3>
          <div className="mt-4 space-y-3">
            {reveal.attribution.map((a) => {
              const mine = a.functionKey === myFunctionKey;
              return (
                <div
                  key={a.functionKey}
                  className={`rounded-2xl border-2 p-4 ${mine ? 'border-primary-400 bg-primary-50' : 'border-neutral-200 bg-white'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wide text-neutral-500">{a.functionName}</span>
                    {mine && <span className="rounded bg-primary-500 px-1.5 py-0.5 text-[10px] font-bold text-white">YOU</span>}
                  </div>
                  <p className="mt-1.5 text-[15px] font-semibold text-neutral-900">{a.card?.title}</p>
                  <p className="mt-0.5 text-sm text-neutral-600">{a.card?.description}</p>
                </div>
              );
            })}
          </div>

          {reveal.event && (
            <div className="mt-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700">
                {view.meta?.eventDeckLabel}
              </p>
              <p className="mt-1 text-[15px] font-semibold text-neutral-900">{reveal.event.title}</p>
              <p className="mt-0.5 text-sm text-neutral-700">{reveal.event.description}</p>
            </div>
          )}

          <div className="mt-5 rounded-2xl bg-neutral-900 p-4">
            <p className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">What it did to the numbers</p>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {(view.kpis || []).map((kpi) => {
                const d = reveal.total[kpi.key];
                const sense = movementSense(kpi, d);
                return (
                  <div key={kpi.key}>
                    <p className="text-[10px] uppercase tracking-wide text-neutral-500">{kpi.label}</p>
                    <p
                      className={`text-lg font-bold tabular-nums ${
                        sense === 'good' ? 'text-emerald-400' : sense === 'bad' ? 'text-red-400' : 'text-neutral-300'
                      }`}
                    >
                      {formatDelta(d)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="mt-4 text-center text-xs text-neutral-400">
            What these choices say about how we work together comes in the final debrief.
          </p>
        </>
      )}

      {/* ---------------- CLOSED ---------------- */}
      {phase === 'closed' && (
        <div className="py-10 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-50">
            <svg className="h-8 w-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="mt-4 text-xl font-bold text-neutral-900">Look at the main screen</h3>
          <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-neutral-600">
            The simulation is over. Your trainer is going through how every round went, where
            the numbers ended up, and what the choices say.
          </p>
          {onBackToSession && (
            <button
              type="button"
              onClick={onBackToSession}
              className="mt-6 rounded-xl border border-neutral-300 px-5 py-2.5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              Back to the session
            </button>
          )}
          <p className="mt-3 text-xs text-neutral-400">
            This page moves on by itself when your trainer starts the next activity.
          </p>
        </div>
      )}
    </Shell>
  );
};

SimulationActivity.propTypes = {
  /** The redacted participant view — server-built in a live run. */
  view: PropTypes.object.isRequired,
  roundIndex: PropTypes.number,
  phase: PropTypes.oneOf(['brief', 'negotiate', 'decide', 'locked', 'reveal', 'debrief', 'closed']),
  myFunctionKey: PropTypes.string.isRequired,
  selectedCardId: PropTypes.string,
  lockedCardId: PropTypes.string,
  lockedCount: PropTypes.number,
  reveal: PropTypes.object,
  sessionCode: PropTypes.string,
  /** Seconds left on the negotiate/decide clock; 0 shows "Time's up". */
  timeRemaining: PropTypes.number,
  onSelect: PropTypes.func,
  onLockIn: PropTypes.func,
  onBackToList: PropTypes.func,
  /** Shown on the closed screen; the page also moves on by itself. */
  onBackToSession: PropTypes.func,
};

export default SimulationActivity;
